import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import RoutingRule from "../../../assets/svg/routing/routingRule.svg";
import ResetRule from "../../../assets/svg/routing/eraserIcon.svg";
import EditIcon from "../../../assets/svg/routing/editIcon.svg";
import DeleteIcon from "../../../assets/svg/routing/trash-02.svg";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CustomButton from "../../reusableComponents/CustomButton";
import {
  Block,
  ContentCopy,
  HelpOutline,
  Info,
  TaskAlt,
} from "@mui/icons-material";
import { Checkbox, OutlinedInput, Tooltip, Typography } from "@mui/material";
import CustomSelect from "../../reusableComponents/CustomSelect/CustomSelect";
import { useAuth } from "../../context/API_Client";
import { customToast } from "../../reusableComponents/customToaster/customToast";
import ConditionList from "./Conditions/ConditionList";
import ProcessorList from "./Processor/ProcessorList";
import useDynamicFields from "../../customHooks/useDynamicFields";
import { isEmpty } from "../utils/utils";
import { colorBrandObject } from "../../../constants/brandings";
import FallbackRoutingModal from "./modals/FallbackRoutingModal";
import ConfirmAndReviewModal from "./modals/ConfirmAndReviewModal";
import RuleDetail from "./RuleView/RuleDetail";
import KeyboardControlKeyIcon from "@mui/icons-material/KeyboardControlKey";
import DeleteModal from "./modals/DeleteModal";
import MoreOptionsPopOver from "../../reusableComponents/moreOptionsPopOver/MoreOptionsPopOver";
import ToggleRuleStatusModal from "./modals/ToggleRuleStatusModal";
import ConflictRuleModal from "./modals/ConflictRuleModal";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

const processorInitialState = { pspCode: "", percentage: "" };
const conditionInitialState = { field: "", operator: "", values: [] };

const RouteRule = forwardRef(
  (
    {
      data,
      businessUnitOptions,
      paymentGateways,
      setRulesData,
      isCreateRule,
      setIsCreateRule,
      setShowHelpModal,
      handleCopyAndPastRule,
      dataLength,
      handleChangeOrder,
      index,
      ruleUpdateLoader,
      operationPermissions,
      setIsRuleAvailable
    },
    ref
  ) => {
    const [selectedBusinessUnit, setSelectedBusinessUnit] = useState("");
    const [condition, setCondition] = useState([]);
    const [showProcessors, setShowProcessors] = useState(false);
    const [ruleId, setRuleId] = useState("");
    const [availablePsp, setAvailablePsp] = useState([]);
    const [dynamicRoutingFallback, setDynamicRoutingFallback] = useState(true);
    const [fallbackRoutingModal, setFallbackRoutingModal] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [processorError, setProcessorError] = useState("");
    const [processorLoading, setProcessorLoading] = useState(false);
    const [ruleIdError, setRuleIdError] = useState(false);
    const [splitEqually, setSplitEqually] = useState(false);
    const [isDeleteModal, setIsDeleteModal] = useState(false);
    const [editRule, setEditRule] = useState(false);
    const [editLoader, setEditLoader] = useState(false);
    const [ruleStatusModal, setRuleStatusModal] = useState(false);
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [reorderLoader, setReorderLoader] = useState("");

    const ruleIdRef = useRef(null);
    const {
      routingRuleSuggester,
      addRoutingRules,
      updateRoutingRule,
      isAuthorizedPermission
    } = useAuth();

    const processorDynamicFields = useDynamicFields(processorInitialState);
    const conditionDynamicFields = useDynamicFields(conditionInitialState);

    const areArraysEqual = (arr1, arr2) => {
      // Helper function to compare two objects deeply
      function deepEqual(obj1, obj2) {
        if (obj1 === obj2) return true;

        if (
          typeof obj1 !== "object" ||
          typeof obj2 !== "object" ||
          obj1 === null ||
          obj2 === null
        ) {
          return false;
        }

        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        if (keys1?.length !== keys2?.length) return false;

        for (let key of keys1) {
          if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
            return false;
          }
        }

        return true;
      }

      if (arr1?.length !== arr2?.length) return false;

      for (let i = 0; i < arr1.length; i++) {
        if (!deepEqual(arr1[i], arr2[i])) return false;
      }

      return true;
    };

    const handleConditionFormatFields = (fields, validate = true) => {
      if (validate) {
        if (!fields || fields.length === 0) {
          customToast("error", "high", "All conditions are required");
          return;
        }

        const isInvalid = fields.some(
          ({ field, operator, values }) =>
            !field || !operator || isEmpty(values)
        );
        if (isInvalid) {
          customToast("error", "high", "All conditions are required");
          return;
        }
      }

      // Format the fields
      return fields
        ?.map(({ field, operator, values, values2 }) => {
          const baseFormat = { field, operator: operator || null };
          if (operator?.toLowerCase() === "between") {
            return { ...baseFormat, values: [values || null, values2 || null] };
          }
          if (typeof values === "string") {
            return { ...baseFormat, values: values ? [values] : null };
          }
          return { ...baseFormat, values: values?.length ? values : null };
        })
        .filter(({ field }) => field);
    };

    const handleFormatPspData = (fields) => {
      let formattedFields = fields?.map((el) => ({
        pspCode: el.pspCode,
        percentage: el.percentage,
      }));
      formattedFields = formattedFields.filter((el) => el.pspCode);
      return { pspRoutingParameters: formattedFields, splitEqually };
    };

    const getConditionData = async (value, conditions) => {
      try {
        const payload = {
          legalEntityCode: value,
        };
        if (conditions) {
          payload.criteria = {
            conditions,
          };
        }
        return await routingRuleSuggester(
          payload,
          (data) => {
            if (data?.data) {
              const suggestedCriteria = data.data.suggestedCriteria;
              setAvailablePsp(data.data?.availablePspCodes || []);
              if (!data.data?.availablePspCodes?.length)
                return customToast(
                  "error",
                  "high",
                  "There are no PSP available please remove some conditions "
                );
              setCondition(suggestedCriteria?.conditions || []);
              if (suggestedCriteria.conditions?.length > 0) {
                return suggestedCriteria.conditions;
              }
            }
          },
          (err) => {
            return false;
          }
        );
      } catch (err) { }
    };

    const handleBusinessUnitChange = (value) => {
      setSelectedBusinessUnit(value);
      setCondition([]);
      getConditionData(value);

      if (
        (!isEmpty(processorDynamicFields?.fields) &&
          processorDynamicFields?.fields[0].pspCode) ||
        (!isEmpty(conditionDynamicFields?.fields) &&
          conditionDynamicFields?.fields[0]?.field)
      ) {
        processorDynamicFields.resetFields();
        conditionDynamicFields.resetFields();
        setShowProcessors(false);
        customToast(
          "warning",
          "medium",
          "Changing business units will reset your rule configuration. To undo the changes, click the 'Reset Rule' button."
        );
      }
    };

    const handleSubmitForm = async (
      status = "ACTIVE",
      validate = true,
      closeForm
    ) => {
      if (!ruleId && validate) {
        return customToast("error", "high", "Rule name is required");
      }
      const formattedFields = handleConditionFormatFields(
        conditionDynamicFields.fields,
        validate
      );
      if (!formattedFields && validate) {
        return customToast(
          "error",
          "high",
          "At least one condition is required to create a rule"
        );
      }
      const routeTo = handleFormatPspData(processorDynamicFields.fields);
      if (!routeTo && validate) {
        return customToast(
          "error",
          "high",
          "At least one PSP is required to create a routing rule"
        );
      }
      const payload = {
        name: ruleId || `Untitled Rule ${dataLength || 1} `,
        routeTo: routeTo?.pspRoutingParameters?.length ? routeTo : null,
        criteria: formattedFields?.length
          ? { conditions: formattedFields }
          : null,
        legalEntityCode: selectedBusinessUnit,
        status,
        dynamicRoutingFallback,
      };
      const handleSuccess = (response) => {
        const updatedData = response?.data;
        setShowConfirmationModal(false);

        if (data?.ruleId) {
          setRulesData((state) =>
            state.map((el) =>
              el?.ruleId == updatedData?.ruleId ? updatedData : el
            )
          );
          setEditRule(false);
        } else {
          setIsRuleAvailable(true)
          setRulesData((state) => [...state, updatedData]);
          if (validate || closeForm) {
            setEditRule(false);
            setIsCreateRule(false);
          }
        }
      };

      const handleError = (errMsg, action, err) => {
        if (err !== 409) {
          customToast(
            "error",
            "high",
            errMsg || `Something went wrong while ${action} the rule`
          );
        } else if (err == 409) {
          setShowConflictModal(true);
        }
        setShowConfirmationModal(false);
      };

      if (data?.ruleId) {
        await updateRoutingRule(
          data.ruleId,
          payload,
          handleSuccess,
          (errMsg, err) => handleError(errMsg, "updating", err)
        );
      } else {
        await addRoutingRules(payload, handleSuccess, (errMsg, err) =>
          handleError(errMsg, "creating", err)
        );
      }
    };

    const handleCheckboxChanges = (e) => {
      const checked = e?.target?.checked;
      if (!checked) {
        setFallbackRoutingModal(true);
      } else {
        setDynamicRoutingFallback(checked);
      }
    };

    const disableDynamicRouting = () => {
      setDynamicRoutingFallback(false);
      setFallbackRoutingModal(false);
    };

    const addMoreCondition = async (sendEmpty) => {
      const formattedFields = sendEmpty
        ? null
        : handleConditionFormatFields(conditionDynamicFields?.fields || []);
      if (!formattedFields && !sendEmpty) return false;
      return getConditionData(selectedBusinessUnit, formattedFields);
    };

    const handleAddProcessor = async () => {
      setProcessorLoading(true);
      const isValid = await addMoreCondition();
      setProcessorLoading(false);
      if (isValid) {
        setShowProcessors(true);
      }
    };

    const handleResetProcessor = () => {
      processorDynamicFields.resetFields();
      setShowProcessors(false);
    };

    const submitAvailable = () => {
      return (
        processorDynamicFields?.fields?.every(
          (field) =>
            field?.pspCode &&
            field?.percentage &&
            !field?.error &&
            !processorError
        ) &&
        processorDynamicFields.fields?.reduce(
          (sum, item) => sum + (item.percentage * 1 || 0),
          0
        ) == 100
      );
    };

    const handleOpenSubmitModal = () => {
      if (ruleId) {
        setShowConfirmationModal(true);
      } else {
        ruleIdRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        setRuleIdError(true);
      }
    };
    const handleChangeRuleId = (e) => {
      if (e?.target?.value) {
        setRuleIdError(false);
      } else {
        setRuleIdError(true);
      }
      setRuleId(e?.target?.value || "");
    };

    const handleEditForm = async () => {
      setEditLoader(true);
      await getConditionData(data?.legalEntityCode, data?.criteria?.conditions);
      setEditLoader(false);
      setEditRule(true);
    };

    const handleResetRule = (resetData) => {
      let conditions = [conditionInitialState];
      let processors = [processorInitialState];
      if (data) {
        conditions = data?.criteria?.conditions;
        if (conditions?.length) {
          conditions = conditions.map((el) => {
            if (el.operator?.toLowerCase() == "between") {
              return { ...el, values: el.values[0], values2: el.values[1] };
            }
            return el;
          });
        }
        setShowProcessors(true);
        processors = data?.routeTo?.pspRoutingParameters;
        setSelectedBusinessUnit(data?.legalEntityCode);
        setRuleId(data?.name);
      } else {
        setRuleId("");
        setSelectedBusinessUnit("");
      }

      processorDynamicFields.resetFields(processors);

      conditionDynamicFields.resetFields(conditions);
    };

    const handleGetStatus = (data) => {
      const statusMap = {
        active: "active",
        draft: "draft",
      };

      if (!data) return "disabled";
      return statusMap[data?.toLowerCase()] || "disabled";
    };

    const handleCloseForm = () => {
      const checkUpdateRequired = () => {
        if (!data) return false;
        const isNameDifferent = data.name !== ruleId;
        const isBusinessUnitDifferent =
          data?.legalEntityCode !== selectedBusinessUnit;
        if (isNameDifferent || isBusinessUnitDifferent) {
          return true;
        }
        const formattedFields = handleConditionFormatFields(
          conditionDynamicFields.fields,
          false
        );
        const areConditionsEqual = areArraysEqual(
          data?.criteria?.conditions,
          formattedFields
        );

        if (!areConditionsEqual) {
          return true;
        }
        const formattedPsp = handleFormatPspData(
          processorDynamicFields.fields,
          false
        );
        const arePspConfigEqual = areArraysEqual(
          data?.routeTo?.pspRoutingParameters,
          formattedPsp?.pspRoutingParameters
        );

        return !arePspConfigEqual;
      };
      if (isCreateRule || checkUpdateRequired()) {
        handleSubmitForm("DRAFT", false, true);
      } else {
        setEditRule(false);
      }
    };
    const handleGetOptionData = () => {
      const items = [
        {
          id: "copyRule",
          label: (
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
              <ContentCopy sx={{ fontSize: "16px" }} />
              <Typography>Duplicate </Typography>
            </div>
          ),
          value: { type: "copy" },
          enable: true,
        },
      ];

      if (data?.status?.toLowerCase() == "active") {
        items.push({
          id: "disabled",
          label: (
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
              <Block sx={{ fontSize: "16px" }} />
              <Typography>Disable</Typography>
            </div>
          ),
          value: { type: "status", value: "DISABLED" },
          enable: true,
        });
      } else if (data?.status?.toLowerCase() == "disabled") {
        items.push({
          id: "active",
          label: (
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
              <TaskAlt sx={{ fontSize: "16px" }} />
              <Typography>Activate </Typography>
            </div>
          ),
          value: { type: "status", value: "ACTIVE" },
          enable: true,
        });
      }
      items.push({
        id: "delete",
        label: (
          <div className="d-flex align-items-center" style={{ gap: "8px" }}>
            <img src="/assets/routingIcons/trashIcon.svg" />
            <Typography>Delete Rule</Typography>
          </div>
        ),
        value: { type: "delete" },
        enable: true,
      });

      return items;
    };

    const handleSetForm = () => {
      if (data) {
        setSelectedBusinessUnit(data?.legalEntityCode);
        setDynamicRoutingFallback(data?.dynamicRoutingFallback);
        setRuleId(data.name);
        if (!isEmpty(data?.criteria?.conditions)) {
          let conditions = data?.criteria?.conditions;
          if (conditions?.length) {
            conditions = conditions.map((el) => {
              if (el.operator?.toLowerCase() == "between") {
                return { ...el, values: el.values[0], values2: el.values[1] };
              }
              return el;
            });
          }
          conditionDynamicFields.resetFields(conditions);
        }
        if (!isEmpty(data?.routeTo?.pspRoutingParameters)) {
          setShowProcessors(true);
          setSplitEqually(data?.routeTo?.splitEqually || false);
          processorDynamicFields.resetFields(data.routeTo.pspRoutingParameters);
        }
        if (isCreateRule) {
          getConditionData(data?.legalEntityCode, data?.criteria?.conditions);
        }
      }
    };

    const handleReorder = async (action) => {
      setReorderLoader(action);
      await handleChangeOrder(action);
      setReorderLoader("");
    };

    const handleActionSelect = (actionData) => {
      if (actionData.type == "copy") {
        handleCopyAndPastRule(data);
      } else if (actionData.type == "status") {
        setRuleStatusModal(true);
      } else if (actionData.type == "delete") {
        setIsDeleteModal(true);
      }
    };

    useImperativeHandle(ref, () => ({
      submitForm: handleSubmitForm,
      businessUnit: selectedBusinessUnit,
      resetData: handleResetRule,
    }));

    useEffect(() => {
      if (isCreateRule) {
        setEditRule(true);
      }
      handleSetForm();
    }, [data]);

    return (
      <>
        <div className="containerBorder" style={{ margin: "20px 0" }}>
          <div
            className="d-flex p-3  align-items-center justify-content-between  "
            style={{ borderBottom: "1px solid #D9D9D9" }}
          >
            <div className="d-flex  align-items-center" style={{ gap: "10px" }}>
              <RoutingRule color="#000" height={24} width={24} />

              <p className="ruleHeading">
                <>
                  {ruleId ? (
                    <Tooltip title={ruleId}>
                      {ruleId.length > 24
                        ? ruleId.substring(0, 24) + "..."
                        : ruleId}
                    </Tooltip>
                  ) : (
                    "New Rule"
                  )}
                </>
                {data?.status && (
                  <span
                    className={`ruleStatus ${handleGetStatus(data?.status)}`}
                  >
                    {data?.status}
                  </span>
                )}
              </p>
            </div>

            <div className="d-flex " style={{ gap: "10px" }}>
              <CustomButton
                onClick={() => setShowHelpModal(true)}
                text={
                  <span style={{ fontSize: "14px" }}>
                    <HelpOutline style={{ fontSize: "18px" }} /> Help
                  </span>
                }
                variant={"outlined"}
                color={"primary"}
              />
              {editRule && (
                <>
                  <CustomButton
                    onClick={handleResetRule}
                    text={
                      <span style={{ fontSize: "14px" }}>
                        <ResetRule width={16} /> Reset Rule
                      </span>
                    }
                    variant={"outlined"}
                    color={"primary"}
                  />
                  {isCreateRule && (
                    <CustomButton
                      onClick={(closeRule) => setIsCreateRule(false)}
                      sx={{ padding: "5px 8px" }}
                      text={<DeleteIcon width={16} />}
                      variant={"outlined"}
                      color={"primary"}
                    />
                  )}
                </>
              )}
              {!editRule && (
                <>
                  {
                    isAuthorizedPermission(operationPermissions?.updateRoutingRule) && (
                      <CustomButton
                        loading={editLoader}
                        disabled={ruleUpdateLoader}
                        text={
                          <span style={{ fontSize: "14px" }}>
                            <EditIcon width={16} /> Edit Rule
                          </span>
                        }
                        onClick={handleEditForm}
                        variant={"outlined"}
                        color={"primary"}
                      />
                    )
                  }

                  <CustomButton
                    loading={reorderLoader == "down"}
                    disabled={index == dataLength - 1 || ruleUpdateLoader}
                    text={
                      <ArrowDownwardIcon
                        style={{
                          color:
                            index == dataLength - 1 || ruleUpdateLoader
                              ? "#c6bfbf"
                              : "#403E9F",
                        }}
                      />
                    }
                    onClick={() => handleChangeOrder("down")}
                    variant={"outlined"}
                    color={"primary"}
                    sx={{ padding: "5px" }}
                  />
                  <CustomButton
                    loading={reorderLoader == "up"}
                    disabled={index == 0 || ruleUpdateLoader}
                    text={
                      <ArrowUpwardIcon
                        style={{
                          color:
                            index == 0 || ruleUpdateLoader
                              ? "#c6bfbf"
                              : "#403E9F",
                        }}
                      />
                    }
                    onClick={() => handleReorder("up")}
                    variant={"outlined"}
                    color={"primary"}
                    sx={{ padding: "5px" }}
                  />

                  <div className="headerOptionContainer">
                    <MoreOptionsPopOver
                      items={handleGetOptionData()}
                      handleActionSelect={handleActionSelect}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <div>
            {editRule ? (
              <div>
                <div
                  className="p-4"
                  style={{ minHeight: editRule ? "400px" : "auto" }}
                >
                  <div className="d-flex " style={{ gap: 25 }}>
                    <div style={{ width: "40%" }}>
                      <Typography
                        className="labelRegular mb-2"
                        style={{
                          color: "var(--global-neutral-grey-1100, #2D2B32)",
                        }}
                      >
                        Rule Name *
                      </Typography>

                      <OutlinedInput
                        // className={`routeRuleName ${ruleIdError && "ruleIdError"} `}
                        placeholder="Enter Rule Name *"
                        className="textFieldCreate"
                        size="small"
                        ref={ruleIdRef}
                        onChange={handleChangeRuleId}
                        value={ruleId}
                        error={ruleIdError}
                        style={{ width: "100%", borderRadius: "10px" }}
                      />
                      {ruleIdError && (
                        <div className="percentage-error-routing">
                          <ErrorOutlineIcon />
                          <Typography>Rule Name is required</Typography>
                        </div>
                      )}
                    </div>
                    <div style={{ width: "40%" }}>
                      <Typography
                        className="labelRegular mb-2"
                        style={{
                          color: "var(--global-neutral-grey-1100, #2D2B32)",
                        }}
                      >
                        Business Unit *
                      </Typography>

                      <CustomSelect
                        options={businessUnitOptions}
                        value={selectedBusinessUnit}
                        onChange={handleBusinessUnitChange}
                        placeholder="Select Business Unit *"
                        style={{ width: "100%", borderRadius: "10px" }}
                      />
                    </div>
                  </div>

                  {selectedBusinessUnit && !isEmpty(condition) && (
                    <div>
                      <p className="mt-2 mb-0 timelineText">For</p>

                      <div className="timelineContainer">
                        <ConditionList
                          businessUnit={selectedBusinessUnit}
                          condition={condition}
                          conditionDynamicFields={conditionDynamicFields}
                          addMoreCondition={addMoreCondition}
                          handleResetProcessor={handleResetProcessor}
                          processors={showProcessors}
                          availablePsp={availablePsp}
                        />

                        <div
                          style={{ position: "relative", height: "20px" }}
                          className="mb-4"
                        >
                          <div className="timelineSeparator timelineText">
                            Route To
                          </div>
                        </div>
                      </div>

                      <div className="my-2" style={{ position: "" }}>
                        {showProcessors ? (
                          <ProcessorList
                            processors={availablePsp}
                            processorDynamicFields={processorDynamicFields}
                            error={processorError}
                            setError={setProcessorError}
                            splitEqually={splitEqually}
                            setSplitEqually={setSplitEqually}
                          />
                        ) : (
                          <CustomButton
                            loading={processorLoading}
                            containerStyle={{ display: "inline-block" }}
                            text={"Add Processor"}
                            type={"primary"}
                            variant={"contained"}
                            onClick={handleAddProcessor}
                            sx={{ background: "#E4E4FF", color: "#403E9F" }}
                          />
                        )}
                      </div>
                    </div>
                  )}
                  {processorDynamicFields?.fields?.length > 0 &&
                    selectedBusinessUnit &&
                    !isEmpty(condition) && (
                      <div
                        className="mt-2 d-flex align-items-center"
                        style={{ gap: 10 }}
                      >
                        <Checkbox
                          sx={{
                            "& .MuiSvgIcon-root": {
                              color: colorBrandObject?.primary,
                            },
                            "&.Mui-checked": {
                              color: colorBrandObject?.primary,
                              "& .MuiSvgIcon-root": {
                                fill: colorBrandObject?.primary,
                              },
                            },
                          }}
                          style={{
                            fontFamily: "Poppins",
                            fontWeight: "500",
                            fontSize: "14px",
                            height: "18px",
                            color: "rgba(36, 183, 170, 1)",
                            marginRight: "8px",
                          }}
                          id={"dynamicRouting"}
                          checked={!!dynamicRoutingFallback}
                          value={dynamicRoutingFallback}
                          onChange={handleCheckboxChanges}
                        />
                        <span className="dynamicRoutingText">
                          Enable Dynamic Routing as Fallback
                        </span>

                        <Tooltip title={"Enable Dynamic Routing ensures transactions are automatically redirected to the best alternative payment provider if the primary route fails, maximizing success rates."}>
                          <Info sx={{ color: "#7F7D83", fontSize: "16px", cursor: "pointer" }} />
                        </Tooltip>
                      </div>
                    )}
                </div>

                <hr className="m-0" />
                <div className="text-right p-3 position-relative ">
                  {data && (
                    <KeyboardControlKeyIcon
                      onClick={handleCloseForm}
                      className="closeEditIcon"
                      sx={{ fontSize: "40px", color: "#7F7D83" }}
                    />
                  )}

                  <CustomButton
                    text={"Confirm & Review"}
                    type={"primary"}
                    variant={"contained"}
                    disabled={!submitAvailable()}
                    onClick={handleOpenSubmitModal}
                    containerStyle={{ display: "inline-block" }}
                  />
                </div>
              </div>
            ) : (
              <RuleDetail
                data={data}
                businessUnitOptions={businessUnitOptions}
                paymentGateways={paymentGateways}
              />
            )}
          </div>

          {fallbackRoutingModal && (
            <FallbackRoutingModal
              fallbackRoutingModal={fallbackRoutingModal}
              setFallbackRoutingModal={setFallbackRoutingModal}
              onClick={disableDynamicRouting}
            />
          )}

          {showConfirmationModal && (
            <ConfirmAndReviewModal
              isOpen={showConfirmationModal}
              setIsOpen={setShowConfirmationModal}
              submitForm={handleSubmitForm}
            />
          )}
          {isDeleteModal && (
            <DeleteModal
              isOpen={isDeleteModal}
              setIsOpen={setIsDeleteModal}
              dataLength={dataLength}
              setIsRuleAvailable={setIsRuleAvailable}
              ruleId={data?.ruleId}
              setRuleData={setRulesData}
            />
          )}
          {showConflictModal && (
            <ConflictRuleModal
              isOpen={showConflictModal}
              setIsOpen={setShowConflictModal}
            />
          )}

          {ruleStatusModal && (
            <ToggleRuleStatusModal
              data={data}
              setIsOpen={setRuleStatusModal}
              isOpen={ruleStatusModal}
              setRuleData={setRulesData}
            />
          )}
        </div>
      </>
    );
  }
);

export default RouteRule;
